import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useI18n } from '../lib/i18n';
import { CalendarBlank, DownloadSimple } from '@phosphor-icons/react';
import { CustomFields } from '../components/acad/CustomFields';
import { DegreeAudit } from '../components/acad/DegreeAudit';
import { ProgressionView } from '../components/acad/ProgressionView';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const BADGE = { registered: 'bg-green-100 text-green-700', waitlisted: 'bg-amber-100 text-amber-700', offered: 'bg-blue-100 text-blue-700', dropped: 'bg-slate-100 text-slate-400', withdrawn: 'bg-rose-100 text-rose-600' };

export default function CourseRegistration() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['acad-me'], retry: false, queryFn: () => api.get('/acad/me').then((r) => r.data) });
  const { data: open } = useQuery({ queryKey: ['reg-catalog'], queryFn: () => api.get('/reg/catalog').then((r) => r.data) });
  const { data: mine } = useQuery({ queryKey: ['acad-my-regs'], queryFn: () => api.get('/acad/my-registrations').then((r) => r.data).catch(() => []) });
  const { data: tt } = useQuery({ queryKey: ['acad-my-tt'], queryFn: () => api.get('/acad/my-timetable').then((r) => r.data).catch(() => []) });
  const [custom, setCustom] = useState({});
  const refresh = () => { ['acad-my-regs', 'acad-my-tt', 'reg-catalog'].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
  const register = async (section_id) => {
    try {
      const { data } = await api.post('/reg/register', { section_ids: [section_id], custom });
      if (!data.ok) {
        const ff = data.failed?.[0]?.first_failure;
        toast.error(ff ? ff.message : 'This registration is blocked', { description: ff?.action });
        return;
      }
      const r = data.registered[0];
      const feeNote = r.fee_charged ? ` - tuition ${r.fee_charged} added to your term invoice` : '';
      toast[r.status === 'waitlisted' ? 'warning' : 'success'](
        r.status === 'waitlisted' ? `The section is full - you are #${r.waitlist_position} on the waitlist` : `You are registered${feeNote}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const drop = async (rid) => {
    try {
      const { data } = await api.post('/reg/drop', { registration_id: rid, reason: 'Dropped by the student' });
      toast.success(data.outcome === 'withdrawn' ? `Recorded as a withdrawal (grade code ${data.grade_code})` : 'Dropped');
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const respond = async (rid, accept) => {
    try {
      const { data } = await api.post('/reg/waitlist/respond', { registration_id: rid, accept });
      toast.success(accept ? `Seat accepted - you are registered${data.fee_charged ? ` (tuition ${data.fee_charged})` : ''}` : 'Seat passed to the next student');
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const activeIds = new Set((mine || []).filter((r) => r.status !== 'dropped').map((r) => r.section_id));
  return (
    <div className="space-y-5" data-testid="course-registration-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><CalendarBlank size={26} className="text-orange-600" />{t('Course registration')}</h1>
          <p className="text-sm text-slate-500">{t('Register for open sections, watch your waitlist spot and see your week.')}</p>
        </div>
        {me?.student_id && (
          <a data-testid="my-transcript-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/acad/transcript/${me.student_id}/pdf`} target="_blank" rel="noreferrer"
            className="text-sm border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5"><DownloadSimple size={15} /> {t('My transcript')}</a>
        )}
      </div>
      <Card testid="open-sections">
        <p className="font-display font-bold mb-2">{t('Open for registration')}</p>
        <CustomFields entity="registration" value={custom} onChange={setCustom} />
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {(open || []).map((s) => {
            const seatsLeft = Math.max(0, (s.capacity || 0) - (s.enrolled_count || 0));
            const already = s.already || activeIds.has(s.id);
            const blocked = !s.eligible && !already;
            return (
              <div key={s.id} className="border border-slate-200 rounded-lg p-3 text-sm flex items-start justify-between gap-2" data-testid={`open-section-${s.id}`}>
                <div>
                  <b>{s.course_code} {s.section_code}</b> <span className="text-xs text-slate-400">{s.term_code}</span>
                  <p className="text-xs text-slate-500">{s.course_title}</p>
                  <p className="text-xs text-slate-500">{(s.meeting_pattern || []).map((m) => `${m.day} ${m.start}-${m.end}`).join(', ')} · {s.room || s.delivery_mode}</p>
                  <p className={`text-xs mt-0.5 ${seatsLeft === 0 ? 'text-amber-600' : 'text-green-700'}`}>{seatsLeft === 0 ? `Full - waitlist open (${s.waitlist_capacity})` : `${seatsLeft} seat(s) left`}{s.tuition_fee ? ` · tuition ${s.tuition_fee}` : ''}</p>
                  {blocked && (
                    <p className="text-xs mt-1 text-rose-600" data-testid={`why-not-${s.id}`}>{s.why_not}
                      {s.what_to_do && <span className="block text-slate-400">{s.what_to_do}</span>}</p>
                  )}
                </div>
                <Button data-testid={`register-btn-${s.id}`} size="sm" disabled={already || blocked} className="bg-slate-900 hover:bg-slate-800" onClick={() => register(s.id)}>{already ? 'On it' : blocked ? 'Not eligible' : s.would_waitlist ? 'Join waitlist' : 'Register'}</Button>
              </div>
            );
          })}
        </div>
        {(open || []).length === 0 && <p className="text-sm text-slate-400">{t('Nothing is open for registration right now.')}</p>}
      </Card>
      <Card testid="my-registrations">
        <p className="font-display font-bold mb-2">{t('My registrations')}</p>
        {(mine || []).map((r) => (
          <div key={r.id} className="border-b border-slate-50 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`my-reg-${r.id}`}>
            <span><b>{r.course_code} {r.section_code}</b> {r.course_title} <span className={`text-[10px] px-1.5 py-0.5 rounded ${BADGE[r.status] || 'bg-blue-100 text-blue-700'}`}>{r.status}{r.waitlist_position ? ` #${r.waitlist_position}` : ''}</span>
              {r.status === 'offered' && r.offer_expires_at && <span className="block text-xs text-blue-700">A seat is being held for you until {r.offer_expires_at.slice(0, 16).replace('T', ' ')}</span>}
            </span>
            {r.status === 'offered' ? (
              <span className="flex gap-2 shrink-0">
                <button data-testid={`offer-accept-${r.id}`} onClick={() => respond(r.id, true)} className="text-xs text-green-700 underline">Accept seat</button>
                <button data-testid={`offer-decline-${r.id}`} onClick={() => respond(r.id, false)} className="text-xs text-slate-400 underline">Decline</button>
              </span>
            ) : !['dropped', 'withdrawn'].includes(r.status) && <button data-testid={`my-drop-${r.id}`} onClick={() => drop(r.id)} className="text-xs text-rose-500 underline">Drop</button>}
          </div>
        ))}
        {(mine || []).length === 0 && <p className="text-sm text-slate-400">{t('You have no registrations yet.')}</p>}
      </Card>
      <Card testid="my-degree-progress">
        <p className="font-display font-bold mb-2">{t('My degree progress')}</p>
        {me?.student_id ? <><DegreeAudit studentId={me.student_id} /><ProgressionView studentId={me.student_id} /></> : <p className="text-sm text-slate-400">Your degree audit appears once your login is linked to a student record.</p>}
      </Card>
      <Card testid="my-timetable">
        <p className="font-display font-bold mb-2">{t('My week')}</p>
        {DAYS.filter((d) => (tt || []).some((r) => r.day === d)).map((d) => (
          <div key={d} className="mb-2">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{d}</p>
            {(tt || []).filter((r) => r.day === d).map((r) => <p key={`${r.course_code}-${r.start}`} className="text-sm py-0.5">{r.start}-{r.end} · <b>{r.course_code} {r.section_code}</b>{r.room ? ` · ${r.room}` : ''}</p>)}
          </div>
        ))}
        {(tt || []).length === 0 && <p className="text-sm text-slate-400">{t('Your timetable fills in once you are registered.')}</p>}
      </Card>
    </div>
  );
}
